import { useMutation, useQuery } from '@zg-rentals/react-query5';
import { useDispatch } from 'react-redux';

import { cloneDeep } from 'es-toolkit';

import PropertiesActions from '../../actions/PropertiesActions';

import useRequestConfig from '../useRequestConfig';

import makeRequest from '../../utils/makeRequestUtils';

import ListingCriteria from '../../model/ListingCriteria';
import type { CriterionProps } from '../../model/ListingCriteria/Criterion';

import StringConstants from '../../constants/StringConstants';
import { RENTAL_MANAGER_API_V1_PREFIX } from '../../constants/RouteConstants';

const API_PREFIX = `${RENTAL_MANAGER_API_V1_PREFIX}/users/properties/criteria`;

type ListingCriteriaData = {
  address: Record<string, string>;
  isFeed: boolean;
};

type Data = {
  listingAlias?: string;
  propertyId?: string;
  criteria: Array<Partial<CriterionProps>>;
};

type QueryOptions = {
  onSuccess?: (response: unknown) => void;
  onError?: (err: unknown) => void;
};

/**
 * queryParams = {
 *  propertyId: String,
 * }
 * bodyParams = {
 *  criteria: [
 *      {
 *          type: String,
 *          criterion: String
 *          negotiable: Boolean,
 *      },
 *      ...
 *  ]
 * }
 */
const useSetListingCriteria = (mutationOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig(StringConstants.POST);

  const queryFn = (data: Data) =>
    makeRequest({
      dispatch,
      endpoint: `${API_PREFIX}/set`,
      requestConfig,
      config: {
        params: { propertyId: data.listingAlias || data.propertyId },
        data: { criteria: data.criteria },
      },
    });

  return useMutation({ mutationFn: async (data: Data) => queryFn(data), ...mutationOptions });
};

/**
 * queryParams = {
 *  propertyId: String,
 * }
 */
const useGetListingCriteria = (queryParams: { propertyId: string }, queryOptions: QueryOptions = {}) => {
  const dispatch = useDispatch();
  const requestConfig = useRequestConfig();

  const queryFn = () =>
    makeRequest({
      dispatch,
      endpoint: `${API_PREFIX}/get`,
      requestConfig,
      config: {
        params: { ...queryParams },
        timeout: 5000,
      },
    });

  const queryHook = useQuery<ListingCriteriaData, Error>({
    queryKey: ['useGetListingCriteria', queryParams.propertyId],
    queryFn: async () => {
      try {
        const data = await queryFn();
        queryOptions.onSuccess?.(data);

        const { criteria, isEnabled, price, isFeed } = data;

        dispatch(
          PropertiesActions.updateCurrentProperty({
            listingCriteria: ListingCriteria({
              isEnabled,
              criteria,
            }),
            price,
            isFeed,
          }),
        );

        return cloneDeep(data);
      } catch (err) {
        queryOptions.onError?.(err);
        throw err;
      }
    },
    ...queryOptions,
    staleTime: 0,
    retry: 3,
  });

  return queryHook;
};

export { useSetListingCriteria, useGetListingCriteria };
